import { type FC } from 'react';
import { AdUnit } from './AdUnit.tsx';

import { PRODUCT_TILE_HEIGHT, PRODUCT_TILE_WIDTH } from '../constants.ts';
import { getAdTileSizeVars } from '../utils/getAdTileSizeVars.ts';
import { type OnAbsentAd } from '../hooks/useAbsentAdHandler.ts';
import { type OnSlotRenderEnded, type AdSizes, type AdTileDimensions } from '../types.ts';

import classnames from 'classnames';
import styles from '../styles/ads.scss';

type Props = Partial<AdTileDimensions> & {
    adId: string;
    elementId: string;
    lazyLoad?: boolean;
    sizes?: AdSizes;
    containerClassName?: string;
    onAbsentAd?: OnAbsentAd;
    /** Shrink the creative to the container width instead of letting it overflow. */
    scaleToFit?: boolean;
};

const onSlotRenderEnded: OnSlotRenderEnded = (event, ref, elementId) => {
    const iframe = ref.querySelector('iframe');
    if (event.slot.getSlotElementId() === elementId && iframe) {
        const adHeight = Array.isArray(event.size) ? event.size[1] : null;
        const innerDoc = iframe.contentDocument || iframe.contentWindow?.document;
        const body = innerDoc?.querySelector('body');
        const adImg = innerDoc?.querySelector('img');
        const imgLinkElement = adImg?.parentElement;
        const googleImgDiv = innerDoc?.querySelector('#google_image_div');
        googleImgDiv?.setAttribute('style', 'display: flex;');
        body?.setAttribute('style', 'display: flex; align-items: center; height: 100% !important;');
        innerDoc?.querySelector('html')?.setAttribute('style', 'height: 100%;');
        iframe.setAttribute('width', '100%');
        iframe.setAttribute('height', adHeight ? `${adHeight}px` : '100%');
        adImg?.setAttribute('width', '100%');
        adImg?.setAttribute('height', '100%');
        adImg?.setAttribute('style', 'height: 100%; width: 100%; position: static;');
        imgLinkElement?.setAttribute('style', 'display: flex;');
    }
};

const ProductTileAd: FC<Props> = ({
    adId,
    elementId,
    width = PRODUCT_TILE_WIDTH,
    height = PRODUCT_TILE_HEIGHT,
    lazyLoad = false,
    sizes,
    heightHold,
    widthHold,
    containerClassName,
    onAbsentAd,
    scaleToFit = false,
}) => {
    return (
        <div
            className={classnames(
                containerClassName || styles.tileContainer,
                scaleToFit ? [styles.tileBox, styles.tileContainerScaled] : undefined
            )}
            style={
                scaleToFit ? getAdTileSizeVars({ width, height, heightHold, widthHold }) : undefined
            }
        >
            <div
                className={classnames(styles.tileInner, {
                    [styles.tileInnerScaled]: scaleToFit,
                })}
                data-tn="product-tile-ad"
            >
                <AdUnit
                    adId={adId}
                    elementId={elementId}
                    width={width}
                    height={height}
                    sizes={sizes}
                    heightHold={heightHold}
                    widthHold={widthHold}
                    className={styles.tilePlacement}
                    lazyLoad={lazyLoad}
                    onSlotRenderEnded={(event, ref) => onSlotRenderEnded(event, ref, elementId)}
                    onAbsentAd={onAbsentAd}
                    shouldCollapseOnNoFill
                />
            </div>
        </div>
    );
};

export { ProductTileAd };
